import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Star, Landmark } from "lucide-react";
import { useLang } from "@/hooks/useLang";
import { BUSINESS, UI, SERVICES, trackEvent, fireAdsConversion } from "@/content/site";
import { submitContact } from "@/lib/api";

export default function ServiceSidebar({ sourcePage }) {
  const lang = useLang();
  const ui = UI[lang];
  const [status, setStatus] = useState("idle");
  const [form, setForm] = useState({
    name: "", phone: "", email: "", case_type: "", website: "",
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.case_type) {
      setStatus("error");
      return;
    }
    setStatus("loading");
    trackEvent("form_start", { location: "service_sidebar" });
    try {
      await submitContact({
        ...form,
        message: `Service sidebar inquiry from ${sourcePage}`,
        preferred_language: lang,
        consent: true,
        source_page: sourcePage,
      });
      setStatus("success");
      trackEvent("form_submit", { location: "service_sidebar" });
      // PRIMARY Google Ads conversion — only fires after a successful Mongo
      // write in the API call above. See ContactPage.jsx for rationale.
      fireAdsConversion("formSubmission", { location: "service_sidebar" });
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div className="gw-service-sidebar">
      <div className="gw-service-sidebar-card">
        <span className="gw-eyebrow">{lang === "en" ? "Talk to us" : "Háblenos"}</span>
        <h3>{lang === "en" ? "Request a confidential consultation" : "Solicitar una consulta confidencial"}</h3>
        <p className="gw-muted" style={{fontSize: "0.9rem"}}>{ui.quickResponse}</p>

        <a href={`tel:${BUSINESS.phoneTel}`} className="gw-btn gw-btn-primary" style={{width: "100%", marginBottom: 12, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8}}
          onClick={() => trackEvent("phone_click", { location: "service_sidebar" })}>
          <Phone size={18} strokeWidth={2.2} aria-hidden="true" /> {BUSINESS.phoneDisplay}
        </a>

        {status === "success" ? (
          <div className="gw-service-success">
            <strong>{lang === "en" ? "Thank you!" : "¡Gracias!"}</strong>
            <p>{lang === "en" ? "We'll respond within one business day." : "Le responderemos en un día hábil."}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="gw-service-form">
            {/* Honeypot */}
            <input type="text" name="website" tabIndex="-1" autoComplete="off" value={form.website}
              onChange={(e) => setForm({...form, website: e.target.value})}
              style={{position: "absolute", left: "-9999px"}} aria-hidden="true" />

            <label className="gw-label" htmlFor="sidebar-name">{lang === "en" ? "Name" : "Nombre"} <span className="gw-required">*</span>
              <input id="sidebar-name" name="name" data-testid="sidebar-name-input" className="gw-input" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} autoComplete="name" />
            </label>
            <label className="gw-label" htmlFor="sidebar-phone">{lang === "en" ? "Phone" : "Teléfono"} <span className="gw-required">*</span>
              <input id="sidebar-phone" name="phone" data-testid="sidebar-phone-input" className="gw-input" required type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} autoComplete="tel" />
            </label>
            <label className="gw-label" htmlFor="sidebar-email">Email <span className="gw-required">*</span>
              <input id="sidebar-email" name="email" data-testid="sidebar-email-input" className="gw-input" required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} autoComplete="email" />
            </label>
            <label className="gw-label" htmlFor="sidebar-case-type">{lang === "en" ? "Case Type" : "Tipo de Caso"} <span className="gw-required">*</span>
              <select id="sidebar-case-type" name="case_type" data-testid="sidebar-case-type-select" className="gw-select" required value={form.case_type} onChange={(e) => setForm({...form, case_type: e.target.value})}>
                <option value="">{lang === "en" ? "Select…" : "Seleccione…"}</option>
                {SERVICES.map((s) => (
                  <option key={s.key} value={s.key}>{lang === "en" ? s.en_title : s.es_title}</option>
                ))}
                <option value="business-law">{lang === "en" ? "Business Law" : "Derecho Empresarial"}</option>
                <option value="other">{lang === "en" ? "Other" : "Otro"}</option>
              </select>
            </label>
            <button type="submit" data-testid="sidebar-submit-button" className="gw-btn gw-btn-accent" style={{width: "100%", marginTop: 8}} disabled={status === "loading"}>
              {status === "loading" ? (lang === "en" ? "Sending…" : "Enviando…") : (lang === "en" ? "Request Consultation" : "Solicitar Consulta")}
            </button>
            {status === "error" && <p style={{color: "var(--gw-error)", fontSize: "0.85rem"}}>{lang === "en" ? "Please complete all required fields." : "Por favor complete todos los campos requeridos."}</p>}
          </form>
        )}

        <div className="gw-service-badges">
          <span><Star size={14} strokeWidth={2} aria-hidden="true" /> {lang === "en" ? "5-star reviewed" : "5 estrellas"}</span>
          <span><Landmark size={14} strokeWidth={2} aria-hidden="true" /> {lang === "en" ? "Mexican Consulate Attorney" : "Abogada del Consulado"}</span>
        </div>
      </div>

      <style>{`
        .gw-service-layout {
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: var(--gw-space-8);
          margin-bottom: var(--gw-space-8);
        }
        @media (max-width: 900px) {
          .gw-service-layout { grid-template-columns: 1fr; }
          .gw-service-aside { order: -1; }
        }
        .gw-service-sidebar { position: sticky; top: calc(var(--gw-header-height) + 16px); }
        .gw-service-sidebar-card {
          background: #fff;
          border-radius: var(--gw-radius-lg);
          padding: var(--gw-space-6);
          border: 1px solid var(--gw-border);
          box-shadow: var(--gw-shadow);
        }
        .gw-service-sidebar-card h3 { color: var(--gw-primary-dark); margin-bottom: 4px; }
        .gw-service-form label { margin-top: var(--gw-space-3); font-size: 0.85rem; }
        /* iOS Safari zooms into any input with font-size < 16px, so we lock to 16px
           on mobile and only shrink slightly on desktop. Minimum height stays 48px
           on all viewports so tap targets clear Apple's 44px + Google's 48px specs. */
        .gw-service-form .gw-input, .gw-service-form .gw-select {
          min-height: 48px;
          padding: 0.65rem 0.85rem;
          font-size: 16px;
          margin-top: 4px;
        }
        @media (min-width: 768px) {
          .gw-service-form .gw-input, .gw-service-form .gw-select {
            min-height: 42px;
            font-size: 0.95rem;
          }
        }
        .gw-service-success { padding: var(--gw-space-4); background: #E8F5EC; border-radius: var(--gw-radius); text-align: center; color: var(--gw-success); }
        .gw-service-badges { display: flex; flex-direction: column; gap: 6px; margin-top: var(--gw-space-4); padding-top: var(--gw-space-4); border-top: 1px solid var(--gw-border); font-size: 0.82rem; color: var(--gw-muted); }
        .gw-service-badges span { display: inline-flex; align-items: center; gap: 8px; }
        .gw-service-badges svg { color: var(--gw-accent); flex-shrink: 0; }
      `}</style>
    </div>
  );
}
